<template>
  <section
    ref="containerEl"
    class="relative w-full isolate overflow-hidden select-none"
    aria-label="Головний банер"
    tabindex="0"
    @mouseenter="pause()"
    @mouseleave="play()"
    @keydown.left.prevent="prev"
    @keydown.right.prevent="next"
    @pointerdown="onPointerDown"
    @pointermove="onPointerMove"
    @pointerup="onPointerUp"
    @pointercancel="onPointerCancel"
  >
    <!-- Контейнер зі сталою пропорцією → без стрибків -->
    <div class="relative w-full aspect-[4/5] md:aspect-[16/9]">
      <div
        v-for="{ s, i } in renderedSlides"
        :id="`hero-slide-${i}`"
        :key="i"
        class="absolute inset-0 transition-opacity duration-700 will-change-[opacity,transform]"
        :class="i === current ? 'opacity-100' : 'opacity-0 pointer-events-none'"
        :style="i === current ? dragStyle : null"
        role="group"
        :aria-roledescription="'slide'"
        :aria-label="`Слайд ${i + 1} з ${slides.length}`"
      >
        <picture>
          <source
            v-if="s.desktop"
            :srcset="heroSrcset(s.desktop, HERO_DESKTOP_WIDTHS)"
            media="(min-width: 768px)"
          />
          <source
            v-if="s.mobile"
            :srcset="heroSrcset(s.mobile, HERO_MOBILE_WIDTHS)"
            media="(max-width: 767px)"
          />
          <img
            :src="fallbackSrc(s)"
            :alt="s.alt || 'Hero banner'"
            class="absolute inset-0 w-full h-full object-cover object-center"
            :loading="i === current ? 'eager' : 'lazy'"
            :decoding="i === current ? 'sync' : 'async'"
            :fetchpriority="i === current ? 'high' : 'auto'"
            width="1920"
            height="1080"
          />
        </picture>

        <!-- затемнення -->
        <div
          class="absolute inset-0 bg-gradient-to-t from-black/60 via-black/30 to-transparent"
        ></div>

        <!-- контент -->
        <div class="absolute inset-0 z-10 flex items-center justify-center">
          <div class="px-8 md:px-8 text-center max-w-3xl">
            <h1 class="text-white font-bold leading-tight" :class="titleClass">
              {{ s.title || defaultTitle }}
            </h1>
            <p
              v-if="s.subtitle || defaultSubtitle"
              class="text-white/90 mt-3"
              :class="subtitleClass"
              v-html="s.subtitle || defaultSubtitle"
            ></p>
            <div class="mt-6">
              <NuxtLink
                :to="s.link || ctaLink"
                class="inline-flex items-center justify-center px-6 py-3 rounded-xl font-semibold bg-pink-200 text-black hover:bg-pink-200/80 transition"
                :aria-label="`Перейти за посиланням: ${s.ctaLabel || ctaLabel}`"
              >
                {{ s.ctaLabel || ctaLabel }}
              </NuxtLink>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Нижній fade -->
    <div
      class="pointer-events-none absolute inset-x-0 bottom-0 h-16 md:h-20 bg-gradient-to-t from-black/10 to-transparent"
    ></div>

    <!-- Буліти -->
    <div
      class="absolute z-20 bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2 opacity-80"
      role="tablist"
      aria-label="Слайди героя"
    >
      <button
        v-for="(s, i) in slides"
        :key="'dot-' + i"
        class="h-2.5 w-2.5 rounded-full transition"
        :class="i === current ? 'bg-white' : 'bg-white/50 hover:bg-white/70'"
        role="tab"
        :aria-selected="i === current ? 'true' : 'false'"
        :aria-controls="isRendered(i) ? `hero-slide-${i}` : undefined"
        :title="`Слайд ${i + 1}`"
        :aria-label="`Перейти до слайду ${i + 1}`"
        type="button"
        @pointerdown.stop
        @click="go(i)"
      />
    </div>

    <!-- Стрілки -->
    <button
      type="button"
      class="flex absolute opacity-60 z-20 left-3 sm:left-4 top-1/2 -translate-y-1/2 h-8 w-8 sm:h-12 sm:w-12 items-center justify-center rounded-full bg-white/80 text-gray-900 border border-white/60 shadow-lg backdrop-blur hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-pink-300 active:scale-95 transition"
      aria-label="Попередній слайд"
      title="Попередній"
      @pointerdown.stop
      @click="prev"
    >
      <ChevronLeft :size="22" class="sm:hidden" />
      <ChevronLeft :size="30" class="hidden sm:block" />
    </button>

    <button
      type="button"
      class="flex absolute opacity-60 z-20 right-3 sm:right-4 top-1/2 -translate-y-1/2 h-8 w-8 sm:h-12 sm:w-12 items-center justify-center rounded-full bg-white/80 text-gray-900 border border-white/60 shadow-lg backdrop-blur hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-pink-300 active:scale-95 transition"
      aria-label="Наступний слайд"
      title="Наступний"
      @pointerdown.stop
      @click="next"
    >
      <ChevronRight :size="22" class="sm:hidden" />
      <ChevronRight :size="30" class="hidden sm:block" />
    </button>
  </section>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, computed } from "vue";
import { ChevronLeft, ChevronRight } from "lucide-vue-next";
import { onNuxtReady } from "#app";
import { useImage } from "#imports";
import {
  HERO_MOBILE_WIDTHS,
  HERO_DESKTOP_WIDTHS,
  IMAGE_QUALITY,
} from "~/utils/imageSizes";

/* 🔢 По ОДНОМУ <source> на орієнтацію. Раніше їх було два — webp і
   jpeg — але провайдер `vercel` ігнорує модифікатор format і віддавав
   обом джерелам ІДЕНТИЧНИЙ URL: жодного фолбеку там не було, формат
   домовляється по Accept (avif/webp оголошені в білд-конфізі).

   Ширини — тільки з allowlist (utils/imageSizes.ts). Було
   [480, 640, 750] і [1024, 1280, 1440, 1920, 2560]: половини з них
   немає в image.screens, тож провайдер округляв їх угору і srcset
   виходив із дублікатів та брехливими дескрипторами (запис «1920w»
   вказував на файл 1536px). */
const img = useImage();

function heroSrcset(src, widths) {
  if (!src) return "";
  return widths
    .map(
      (w) =>
        `${img(src, { width: w, format: "webp", quality: IMAGE_QUALITY })} ${w}w`,
    )
    .join(", ");
}

function fallbackSrc(s) {
  const q = { quality: IMAGE_QUALITY };
  if (s.mobile) return img(s.mobile, { width: 768, ...q });
  if (s.desktop) return img(s.desktop, { width: 1280, ...q });
  return "/img/hero/placeholder.jpg";
}

const props = defineProps({
  slides: {
    type: Array,
    default: () => [
      {
        title: "Шовкові наволочки Mulberry",
        subtitle: "Ніжність і турбота про шкіру та волосся щодня.",
        desktop: "/img/hero/slide-1-desktop.jpg",
        mobile: "/img/hero/slide-1-mobile.jpg",
        link: "/catalog",
        ctaLabel: "Купити",
      },
    ],
  },
  interval: { type: Number, default: 6000 },
  ctaLink: { type: String, default: "/catalog" },
  ctaLabel: { type: String, default: "Купити" },
  defaultTitle: {
    type: String,
    default: "Mulberry silk — наволочки та аксесуари",
  },
  defaultSubtitle: { type: String, default: "Made in Ukraine" },
});

const prefersReducedMotion =
  typeof window !== "undefined" &&
  window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;

const current = ref(0);

/* Сусідні слайди (prev/next) тримаємо ПОЗА першим рендером. Вони лежать
   у в'юпорті абсолютом, тож `loading="lazy"` на них не діє: браузер
   тягнув усі три кадри одразу (+52 KB на мобільному) і конкурував ними
   з LCP-кадром. onNuxtReady = після гідрації, в idle; SSR і перший
   клієнтський рендер однакові, тож розходження гідрації немає. */
const neighborsReady = ref(false);
onNuxtReady(() => {
  neighborsReady.value = true;
});

const renderedSet = computed(() => {
  const n = props.slides.length || 0;
  const c = current.value;
  if (n <= 1 || !neighborsReady.value) return new Set([c]);
  const prevIdx = (c - 1 + n) % n;
  const nextIdx = (c + 1) % n;
  return new Set([prevIdx, c, nextIdx]);
});

const renderedSlides = computed(() => {
  const set = renderedSet.value;
  return props.slides.map((s, i) => ({ s, i })).filter(({ i }) => set.has(i));
});

function isRendered(i) {
  return renderedSet.value.has(i);
}

let timer = null;

function next() {
  current.value = (current.value + 1) % props.slides.length;
}
function prev() {
  current.value =
    (current.value - 1 + props.slides.length) % props.slides.length;
}
function go(i) {
  current.value = i;
  play(true);
}
function play(restart = false) {
  if (prefersReducedMotion) return; // повага до налаштувань користувача
  if (restart) pause();
  if (timer) return;
  timer = setInterval(next, props.interval);
}
function pause() {
  if (timer) {
    clearInterval(timer);
    timer = null;
  }
}

onMounted(() => play());
onBeforeUnmount(() => pause());

// Типографіка
const titleClass = computed(() => "text-[clamp(28px,5vw,56px)]");
const subtitleClass = computed(() => "text-[clamp(16px,1.6vw,18px)]");

/* 🔵 SWIPE */
const containerEl = ref(null);
const startX = ref(0);
const startY = ref(0);
const deltaX = ref(0);
const isDown = ref(false);
const lockedToX = ref(false);
const dragStyle = computed(() =>
  isDown.value ? { transform: `translateX(${deltaX.value}px)` } : null,
);

function isInteractiveTarget(e) {
  return !!e.target?.closest?.("button, a, [role='button'], [data-no-swipe]");
}
function onPointerDown(e) {
  if (isInteractiveTarget(e)) return;
  isDown.value = true;
  lockedToX.value = false;
  startX.value = e.clientX;
  startY.value = e.clientY;
  deltaX.value = 0;
  pause();
  try {
    containerEl.value?.setPointerCapture?.(e.pointerId);
  } catch {}
}
function onPointerMove(e) {
  if (!isDown.value) return;
  const dx = e.clientX - startX.value;
  const dy = e.clientY - startY.value;
  if (!lockedToX.value) {
    if (Math.abs(dx) > 8 && Math.abs(Math.abs(dx) - Math.abs(dy)) > 4)
      lockedToX.value = Math.abs(dx) > Math.abs(dy);
  }
  if (!lockedToX.value) return;
  deltaX.value = dx;
}
function onPointerUp() {
  if (!isDown.value) return;
  isDown.value = false;
  const width = containerEl.value?.clientWidth || 1;
  const threshold = Math.max(50, width * 0.12);
  const dx = deltaX.value;
  if (Math.abs(dx) > threshold) {
    if (dx < 0) {
      next();
    } else {
      prev();
    }
  }
  deltaX.value = 0;
  lockedToX.value = false;
  play(true);
}
function onPointerCancel() {
  if (!isDown.value) return;
  isDown.value = false;
  deltaX.value = 0;
  lockedToX.value = false;
  play(true);
}
</script>

<style scoped lang="scss">
section {
  touch-action: pan-y;
  user-select: none;
  cursor: grab;
}
section:active {
  cursor: grabbing;
}
</style>
